import React from "react";

import { faTimes } from "@awesome.me/kit-4c83d50d0d/icons/classic/light";
import bowser from "bowser";
import cx from "classnames";
import cookie from "js-cookie";
import uuidv4 from "uuid/v4";

import { ReactComponent as FellowLogoMono } from "~/legacy-assets/images/logos/fellow-icon-mono.svg";
import { ReactComponent as FellowLogo } from "~/legacy-assets/images/logos/fellow-icon.svg";
import { dispatchAnalyticsTrack } from "~/lib/segment";
import { appStoreLink, playStoreLink } from "~/lib/utils";
import { Button } from "~/ui";
import { IconButton } from "~/ui/legacyFellowIndex";

import styles from "./css/InstallMobileAppPrompt.module.css";

let deferredPrompt: BeforeInstallPromptEvent;

const browser = bowser.getParser(window.navigator.userAgent);
export const MOBILE_PROMPT_KEY = "install_mobile_app_dismissed";

const analyticsProperties = {
	fingerprint: uuidv4(),
	ux: "modal",
	feature: "install_mobile_app",
};

export default function InstallMobileAppPrompt() {
	// Only some mobile browsers support `beforeinstallprompt`
	const nativePrompt =
		browser.is("android") &&
		browser.satisfies({
			mobile: {
				chrome: ">45",
				/* eslint-disable-next-line camelcase */
				samsung_internet: ">5",
				android: ">45",
			},
		});

	const [promptVisible, setPromptVisible] = React.useState(!nativePrompt);
	const [analyticsSent, setAnalyticsSent] = React.useState(false);

	React.useEffect(() => {
		if (promptVisible && !analyticsSent) {
			dispatchAnalyticsTrack("Install Mobile App Nudge Shown", analyticsProperties);
			setAnalyticsSent(true);
		}
	}, [promptVisible, analyticsSent]);

	window.addEventListener("beforeinstallprompt", (e: BeforeInstallPromptEvent) => {
		e.preventDefault();
		deferredPrompt = e;
		setPromptVisible(true);
	});

	const closeModal = React.useCallback(() => {
		setPromptVisible(false);
		cookie.set(MOBILE_PROMPT_KEY, "true", { expires: 2 });
	}, []);

	const dismissModal = React.useCallback(() => {
		closeModal();
		dispatchAnalyticsTrack("Install Mobile App Nudge Dismissed", analyticsProperties);
	}, [closeModal]);

	const completeNudge = React.useCallback(
		(analytics: { [key: string]: string }) => {
			closeModal();
			dispatchAnalyticsTrack("Install Mobile App Nudge Completed", { ...analyticsProperties, ...analytics });
		},
		[closeModal],
	);

	const onClick = React.useCallback(() => {
		if (deferredPrompt) {
			deferredPrompt.prompt();
			deferredPrompt.userChoice.then(completeNudge);
		} else if (browser.is("android")) {
			window.open(playStoreLink);
			completeNudge({ platform: "android" });
		} else if (browser.is("ios")) {
			window.open(appStoreLink);
			completeNudge({ platform: "ios" });
		}
	}, [completeNudge]);

	if (!promptVisible) return null;

	return (
		<div className={styles.body}>
			<div className={styles.inner}>
				<IconButton title="Close" icon={faTimes} className={styles.closeButton} onClick={dismissModal} />
				<div className={styles.logoWrapper}>
					<FellowLogoMono className={cx(styles.logo, styles.logoMono)} />
					<FellowLogo className={styles.logo} />
				</div>
				<div className={styles.info}>
					<div className={styles.appName}>Fellow.app</div>
					<div>Install the mobile app</div>
				</div>
			</div>
			<Button size="wide" variant="primary" onClick={onClick}>
				View
			</Button>
		</div>
	);
}
